import React, { useCallback } from 'react';
import { Tabs, Tab, Box, Tooltip, Stack } from '@mui/material';
import {
  InfoOutlined as InfoOutlinedIcon,
  BusinessCenterOutlined as BusinessCenterOutlinedIcon,
  ContentPasteOutlined as ContentPasteOutlinedIcon,
  LiveHelpOutlined as LiveHelpOutlinedIcon,
  AttachFileOutlined as AttachFileOutlinedIcon,
  Tag as TagIcon,
  FlashOnOutlined as FlashOnOutlinedIcon,
  ForwardOutlined as ForwardOutlinedIcon,
  EmailOutlined as EmailOutlinedIcon,
  Sms as SmsIcon,
  SendToMobile as SendToMobileIcon,
  VerifiedOutlined as VerifiedOutlinedIcon,
  GridOnOutlined as GridOnOutlinedIcon,
  LoopOutlined as LoopOutlinedIcon,
  AssignmentIndOutlined as AssignmentIndOutlinedIcon,
  Google as GoogleIcon
} from '@mui/icons-material';
import AccountClockIcon from '../EditJob/Sources/AccountClockIcon';
import SourcesIconSVG from '../EditJob/Sources/SourcesIconSVG';
import { RobotIcon } from '../../AIStudio/SharedComponents/Graphics/FelixSVG';
import { convertToNormalTabIndex, TabGroup } from './tabIndexUtils';
import { classes } from './styles';
import EditJobToggleLeftSVG from './EditJobToggleLeftSVG';
import EditJobToggleRightSVG from './EditJobToggleRightSVG';
import { formatUserPermissions } from '../Components/Utilities/formatUserPermissions';
import { IJob } from './types';
import { useFeatureFlag } from '../../FeatureFlags';
import { sharedClasses } from '../Components/CustomUIElements/sharedClasses';

export default function EditJobTabs({
  editTabValue,
  handleEditTabChange,
  isToggled,
  setIsToggled,
  job
}: {
  editTabValue?: string | number;
  handleEditTabChange: (event: React.SyntheticEvent, newValue: number) => void;
  isToggled: boolean;
  setIsToggled: (isToggled: boolean) => void;
  job: IJob | null;
}) {
  const userPermissions = formatUserPermissions();
  const tagManagementEnabled = useFeatureFlag('tagManagementEnabled');
  const aiSummariesEnabled = useFeatureFlag('aiSummariesEnabled');
  const aiStudioEnabled = useFeatureFlag('aiStudioEnabled');

  const TabProps = useCallback((tabIndex: number, group: number) => {
    const index = convertToNormalTabIndex(tabIndex, group);
    return {
      id: `vertical-tab-${index}`,
      'aria-controls': `vertical-tabpanel-${index}`,
      'data-testid': `vertical-tab-${index}`
    };
  }, []);

  return (
    <Tabs
      orientation="vertical"
      value={editTabValue}
      variant="scrollable"
      onChange={handleEditTabChange}
      aria-label="Vertical tabs example"
      sx={{
        ...classes.editTabs,
        '& svg': {
          height: '1.5rem',
          marginRight: isToggled ? '0px !important' : '16px !important',
          color: '#084D6D'
        },
        '& .MuiTabs-indicator': { ...classes.editTabIndicator },
        minWidth: isToggled ? '58px !important' : '220px !important',
        transition: 'all 0.5s'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box
          sx={{
            ...classes.tabsSubheader,
            fontSize: isToggled ? 0 : '12px',
            transition: 'all 0.5s'
          }}
        >
          Job Information
        </Box>
        <Box
          sx={{ ...classes.toggleIcon, marginLeft: isToggled ? '6.5px' : 'auto' }}
          onClick={() => setIsToggled(!isToggled)}
        >
          {isToggled ? <EditJobToggleRightSVG /> : <EditJobToggleLeftSVG />}
        </Box>
      </Box>

      <Tooltip placement="right" title={'General'} disableHoverListener={!isToggled} arrow>
        <Tab
          icon={<InfoOutlinedIcon />}
          iconPosition="start"
          label={isToggled ? '' : 'General'}
          {...TabProps(0, TabGroup.JOB_INFORMATION)}
        />
      </Tooltip>
      <Tooltip placement="right" title={'Description'} disableHoverListener={!isToggled} arrow>
        <Tab
          icon={<BusinessCenterOutlinedIcon />}
          iconPosition="start"
          label={isToggled ? '' : 'Description'}
          {...TabProps(1, TabGroup.JOB_INFORMATION)}
        />
      </Tooltip>
      <Tooltip placement="right" title={'Template'} disableHoverListener={!isToggled} arrow>
        <Tab
          icon={<ContentPasteOutlinedIcon />}
          iconPosition="start"
          sx={{
            display: userPermissions?.['Job Options']?.['Edit Job Template']
              ? 'block'
              : 'none !important'
          }}
          label={isToggled ? '' : 'Template'}
          {...TabProps(2, TabGroup.JOB_INFORMATION)}
        />
      </Tooltip>
      <Tooltip placement="right" title={'Questions'} disableHoverListener={!isToggled} arrow>
        <Tab
          icon={<LiveHelpOutlinedIcon />}
          iconPosition="start"
          sx={{
            display: userPermissions?.['Job Options']?.['Create / Edit Job Questions']
              ? 'block'
              : 'none !important'
          }}
          label={isToggled ? '' : 'Questions'}
          {...TabProps(3, TabGroup.JOB_INFORMATION)}
        />
      </Tooltip>
      <Tooltip placement="right" title={'Sources'} disableHoverListener={!isToggled} arrow>
        <Tab
          icon={<SourcesIconSVG />}
          iconPosition="start"
          label={isToggled ? '' : 'Sources'}
          {...TabProps(4, TabGroup.JOB_INFORMATION)}
        />
      </Tooltip>
      <Box
        sx={{
          ...classes.tabsSubheader,
          fontSize: isToggled ? 0 : '12px',
          transition: 'all 0.5s'
        }}
      >
        Candidate Settings
      </Box>
      <Tooltip placement="right" title={'Attachments'} disableHoverListener={!isToggled} arrow>
        <Tab
          icon={<AttachFileOutlinedIcon />}
          iconPosition="start"
          label={isToggled ? '' : 'Attachments'}
          {...TabProps(0, TabGroup.CANDIDATE_SETTINGS)}
        />
      </Tooltip>
      <Box
        sx={{
          ...classes.tabsSubheader,
          fontSize: isToggled ? 0 : '12px',
          transition: 'all 0.5s'
        }}
      >
        Automation
      </Box>
      <Tooltip placement="right" title={'Auto Statuses'} disableHoverListener={!isToggled} arrow>
        <Tab
          icon={
            <FlashOnOutlinedIcon
              sx={{ color: 'transparent !important', stroke: '#084d6d', strokeWidth: '1.8px' }}
            />
          }
          iconPosition="start"
          sx={{
            display: userPermissions?.['Job Options']?.['Manage Auto Statuses']
              ? 'block'
              : 'none !important'
          }}
          label={isToggled ? '' : 'Auto Statuses'}
          {...TabProps(1, TabGroup.AUTOMATION)}
        />
      </Tooltip>
      <Tooltip
        placement="right"
        title={'Status Transitions'}
        disableHoverListener={!isToggled}
        arrow
      >
        <Tab
          icon={<AccountClockIcon />}
          iconPosition="start"
          label={isToggled ? '' : 'Status Transitions'}
          {...TabProps(2, TabGroup.AUTOMATION)}
        />
      </Tooltip>
      <Tooltip placement="right" title={'Auto Forward'} disableHoverListener={!isToggled} arrow>
        <Tab
          icon={<ForwardOutlinedIcon />}
          iconPosition="start"
          sx={{
            display: userPermissions?.['Job Options']?.['Manage Auto Forward configs']
              ? 'block'
              : 'none !important'
          }}
          label={isToggled ? '' : 'Auto Forward'}
          {...TabProps(3, TabGroup.AUTOMATION)}
        />
      </Tooltip>
      <Tooltip placement="right" title={'Auto Email Reply'} disableHoverListener={!isToggled} arrow>
        <Tab
          icon={<EmailOutlinedIcon />}
          iconPosition="start"
          sx={{
            display: userPermissions?.['Job Options']?.['Manage Auto Forward configs']
              ? 'block'
              : 'none !important'
          }}
          label={isToggled ? '' : 'Auto Email Reply'}
          {...TabProps(4, TabGroup.AUTOMATION)}
        />
      </Tooltip>
      <Tooltip placement="right" title={'Auto SMS Reply'} disableHoverListener={!isToggled} arrow>
        <Tab
          icon={<SmsIcon />}
          iconPosition="start"
          sx={{
            display: userPermissions?.['Job Options']?.['Manage Auto Forward configs']
              ? 'block'
              : 'none !important'
          }}
          label={isToggled ? '' : 'Auto SMS Reply'}
          {...TabProps(5, TabGroup.AUTOMATION)}
        />
      </Tooltip>
      <Tooltip placement="right" title={'Auto SMS Forward'} disableHoverListener={!isToggled} arrow>
        <Tab
          icon={<SendToMobileIcon />}
          iconPosition="start"
          sx={{
            display: userPermissions?.['Job Options']?.['Manage Auto Forward configs']
              ? 'block'
              : 'none !important'
          }}
          label={isToggled ? '' : 'Auto SMS Forward'}
          {...TabProps(6, TabGroup.AUTOMATION)}
        />
      </Tooltip>
      <Box
        sx={{
          ...classes.tabsSubheader,
          fontSize: isToggled ? 0 : '12px',
          transition: 'all 0.5s'
        }}
      >
        Advanced
      </Box>
      <Tooltip placement="right" title={'Requisition Info'} disableHoverListener={!isToggled} arrow>
        <Tab
          icon={<VerifiedOutlinedIcon />}
          iconPosition="start"
          sx={{
            display: userPermissions.Jobs?.['Migrate from Requisition Form']
              ? 'block'
              : 'none !important'
          }}
          label={isToggled ? '' : 'Requisition Info'}
          {...TabProps(0, TabGroup.ADVANCED)}
        />
      </Tooltip>
      <Tooltip
        placement="right"
        title={'Configure Application HR Questions'}
        disableHoverListener={!isToggled}
        arrow
      >
        <Tab
          icon={<LiveHelpOutlinedIcon />}
          iconPosition="start"
          sx={{
            display: userPermissions.Jobs?.ConfigureApplicationHRQuestions
              ? 'block'
              : 'none !important'
          }}
          label={isToggled ? '' : 'Configure Application HR Questions'}
          {...TabProps(1, TabGroup.ADVANCED)}
        />
      </Tooltip>
      <Tooltip
        placement="right"
        title={'Automated TestGrid'}
        disableHoverListener={!isToggled}
        arrow
      >
        <Tab
          icon={<GridOnOutlinedIcon />}
          iconPosition="start"
          sx={{
            display:
              job?.integrations?.test_grid_configured && userPermissions.Integrations?.TestGrid
                ? 'block'
                : 'none !important'
          }}
          label={isToggled ? '' : 'Automated TestGrid'}
          {...TabProps(2, TabGroup.ADVANCED)}
        />
      </Tooltip>
      <Tooltip placement="right" title={'Revolve'} disableHoverListener={!isToggled} arrow>
        <Tab
          icon={<LoopOutlinedIcon />}
          iconPosition="start"
          sx={{
            display:
              job?.integrations?.revolve_configured &&
              userPermissions.Integrations?.['Export To Revolve Onboard']
                ? 'block'
                : 'none !important'
          }}
          label={isToggled ? '' : 'Revolve'}
          {...TabProps(3, TabGroup.ADVANCED)}
        />
      </Tooltip>
      <Tooltip
        placement="right"
        title={'Google Conversion Script'}
        disableHoverListener={!isToggled}
        arrow
      >
        <Tab
          icon={<GoogleIcon />}
          iconPosition="start"
          label={isToggled ? '' : 'Google Conversion Script'}
          {...TabProps(4, TabGroup.ADVANCED)}
        />
      </Tooltip>
      <Tooltip placement="right" title={'Panel Workflow'} disableHoverListener={!isToggled} arrow>
        <Tab
          icon={<AssignmentIndOutlinedIcon />}
          iconPosition="start"
          sx={{
            display: job?.entity_settings.show_panel_workflow ? 'block' : 'none !important'
          }}
          label={isToggled ? '' : 'Panel Workflow'}
          {...TabProps(5, TabGroup.ADVANCED)}
        />
      </Tooltip>
      <Tooltip placement="right" title={'AI Studio'} disableHoverListener={!isToggled} arrow>
        <Tab
          icon={<RobotIcon viewBox="0 0 19 19" />}
          iconPosition="start"
          sx={{
            display: aiStudioEnabled && aiSummariesEnabled ? 'block' : 'none !important'
          }}
          label={isToggled ? '' : 'AI Studio'}
          {...TabProps(6, TabGroup.ADVANCED)}
        />
      </Tooltip>
      {tagManagementEnabled && (
        <Box
          sx={{
            ...classes.tabsSubheader,
            fontSize: isToggled ? 0 : '12px',
            transition: 'all 0.5s'
          }}
        >
          New features
        </Box>
      )}
      {tagManagementEnabled && (
        <Tab
          icon={<TagIcon />}
          iconPosition="start"
          sx={{
            display: tagManagementEnabled ? 'block' : 'none !important'
          }}
          label={
            !isToggled && (
              <Stack
                sx={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%'
                }}
              >
                <Stack>Auto Tags</Stack>
                <Box sx={{ ...sharedClasses.applicationBetaButton, boxShadow: 'none' }}>NEW</Box>
              </Stack>
            )
          }
          {...TabProps(0, TabGroup.NEW_FEATURES)}
        />
      )}
    </Tabs>
  );
}
